import React, { useMemo, useState } from 'react';
import { noop } from 'lodash';
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@fiverr-private/navigation';
import { Heading, Typography } from '@fiverr-private/typography';
import { getContext } from '@fiverr-private/fiverr_context';
import { Container } from '@fiverr-private/layout_components';
import { usePersonalizedData } from '../../hooks/usePersonalizedData';
import { extractRecommendationsParams, logger } from '../../shared/logger';
import { useCollections } from '../../hooks/useCollections';
import { ComponentName } from '../../shared/types';
import { tabCarouselHeaderStyle, tabStyles, selectedTabStyles, hideScrollBar } from './TabsView.styles.ve.css';
import { getFullPersonalizedContent } from './utils';
import TabTitle from './TabTitle';
import TabCarouselPanel from './TabCarouselPanel';
import { TabsViewProps } from './types';

const TabsView = (props: TabsViewProps) => {
    const {
        title,
        subtitle,
        subtitleComponent,
        personalizedContent,
        sectionNames,
        appName,
        perseusName,
        onFetchSucceeded,
        onFetchFailed,
        recommendationsParams,
        shouldFetchCollections,
        showTranslatedUGC,
        reportTabClick = noop,
        mobileView = false,
        withBackgroundColor = false,
        variant = 'default',
    } = props;

    logger.setLoggerConsumerData(extractRecommendationsParams(props));

    const { isMobile: isMobileDevice, isTouch } = getContext();
    const isMobile = mobileView || isMobileDevice;
    let tabPaddingLeft: '0' | '8' = '0';

    if (isMobile) {
        tabPaddingLeft = '8';
    }

    const {
        fetchPersonalizedData,
        personalizedData: lazyFetchedPersonalizedData,
        fetchStatus,
    } = usePersonalizedData({
        appName,
        perseusName,
        onFetchSucceeded,
        onFetchFailed,
        recommendationsParams,
    });
    const [selectedTab, setSelectedTab] = useState(0);
    const [, lazySectionName] = sectionNames;
    const [didFetchLazySections, setDidFetchLazySections] = useState(false);
    const fullPersonalizedContent = getFullPersonalizedContent(
        sectionNames,
        personalizedContent,
        lazyFetchedPersonalizedData?.sections
    );
    const didFetchLazySection = lazyFetchedPersonalizedData?.sections?.[lazySectionName]?.[0]?.items?.gigs?.length > 0;
    const didTranslateLazySection = personalizedContent?.[lazySectionName]?.[0]?.items?.gigs?.[0]?.translatedTitle;
    const isWaitingForTranslation = showTranslatedUGC && didFetchLazySection && !didTranslateLazySection;
    const tabStylesObject: Partial<React.ComponentProps<typeof Tab>> = {};
    const isProPcpVariant = variant === 'pro_pcp';

    const getTabClassName = (index: number) => {
        let tabClassName;

        if (isMobile) {
            if (index === selectedTab) {
                tabClassName = selectedTabStyles;
            }

            return tabClassName;
        } else {
            return tabStyles;
        }
    };

    const getVariant = () => {
        if (isMobile) {
            return 'default';
        }

        return 'unstyled';
    };

    const { collectProps } = useCollections({
        shouldFetchCollections,
        appName,
        logAttributes: {
            perseusName,
            recommendationsParams,
            sectionNames,
        },
    });

    const onTabSelect = (selectedIndex) => {
        if (lazySectionName && !didFetchLazySections) {
            fetchPersonalizedData(lazySectionName);
            setDidFetchLazySections(true);
        }

        setSelectedTab(selectedIndex);
        const selectedComponent = fullPersonalizedContent[selectedIndex];

        reportTabClick({
            tab_position: selectedIndex,
            tab_asset: selectedComponent.type,
            tab_logic: selectedComponent.name,
            tab_title:
                selectedComponent.properties?.titleSections?.[0]?.value || selectedComponent.lean_properties?.title,
        });
    };

    const shouldRenderTabs = useMemo(() => {
        const withSections = fullPersonalizedContent
            .map((el) => el?.properties?.titleSections?.[0]?.value || el?.lean_properties?.title)
            .filter(Boolean);

        if (!withSections) {
            return false;
        }

        const recentlyViewedSellers = fullPersonalizedContent.find(
            (component) => component.name === ComponentName.YOUR_RECENTLY_VIEWED_SELLERS
        );

        if (isProPcpVariant && !recentlyViewedSellers?.items?.length) {
            return false;
        }

        return true;
    }, [isProPcpVariant, fullPersonalizedContent]);

    if (!shouldRenderTabs) {
        return null;
    }

    return (
        <Container
            {...(withBackgroundColor && { backgroundColor: 'grey_100', borderRadius: 'sm', padding: '8', gap: '6' })}
        >
            <Container className="personalized-content">
                <Heading
                    data-testid="tabs-carousels-header"
                    as={isMobileDevice ? 'h5' : 'h4'}
                    className={tabCarouselHeaderStyle}
                    paddingLeft="1"
                >
                    {title}
                </Heading>
                {subtitleComponent && (
                    <Typography size="h_xxs" marginTop="8">
                        {subtitleComponent}
                    </Typography>
                )}
                {!subtitleComponent && subtitle && (
                    <Typography size="h_xxs" marginTop="8" className={tabCarouselHeaderStyle}>
                        {subtitle}
                    </Typography>
                )}

                <TabGroup
                    data-testid="tabs-group"
                    onChange={onTabSelect}
                    className="tabs-view"
                    placement={isMobile ? 'top' : 'left'}
                    variant={getVariant()}
                >
                    <TabList
                        data-testid="tab-list"
                        marginTop={{ default: '8', md: '12', lg: '16' }}
                        gap={isMobile ? '3' : '2'}
                        paddingLeft="1"
                        overflowY={isTouch ? 'auto' : 'visible'}
                        width="auto"
                        className={hideScrollBar}
                    >
                        {fullPersonalizedContent
                            .filter((component) => component?.properties || component?.lean_properties)
                            .map((component, index) => (
                                <Tab
                                    data-testid={`tab-title-wrapper-${index}`}
                                    key={`${component.name}-${index}`}
                                    className={getTabClassName(index)}
                                    paddingTop={{ default: '4', lg: '0' }}
                                    paddingLeft={tabPaddingLeft}
                                    whiteSpace={isTouch ? 'nowrap' : 'unset'}
                                    {...tabStylesObject}
                                >
                                    <TabTitle
                                        component={component}
                                        isSelectedTab={index === selectedTab}
                                        mobileView={isMobile}
                                        variant={variant}
                                    />
                                </Tab>
                            ))}
                    </TabList>
                    <TabPanels
                        data-testid="tab-panels"
                        width="100%"
                        overflow="auto"
                        {...(mobileView && { marginTop: '4' })}
                    >
                        {fullPersonalizedContent.map((component, index) => (
                            <TabPanel
                                key={`${component.name}-${index}`}
                                data-testid={`tab-panel-${index}`}
                                marginLeft={isMobile ? '0' : '8'}
                                padding={isMobile ? '0' : '2'}
                            >
                                <TabCarouselPanel
                                    {...props}
                                    collectProps={collectProps}
                                    component={component}
                                    isLoading={fetchStatus.isLoading || isWaitingForTranslation}
                                    isErrorState={fetchStatus.hasError}
                                    variant={variant}
                                />
                            </TabPanel>
                        ))}
                    </TabPanels>
                </TabGroup>
            </Container>
        </Container>
    );
};

export default TabsView;
